import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import moment from 'moment';
import AdminService from '../../services/AdminService';

const WEEKDAYS = [
    { id: 1, label: 'Lun' },
    { id: 2, label: 'Mar' },
    { id: 3, label: 'Mié' },
    { id: 4, label: 'Jue' },
    { id: 5, label: 'Vie' },
    { id: 6, label: 'Sáb' },
    { id: 7, label: 'Dom' },
];

const TAB_CALENDAR = 0;
const TAB_GRID = 1;
const TAB_BUDGET = 2;

const employeePersonKey = (employeeId) => `e:${employeeId}`;
const dayCellKey = (personKey, day) => `${personKey}:${day}`;
/** Asistencia editable solo hasta hoy (inclusive). */
const isAttendanceDayEditable = (day, isWorkingDay) => {
    if (!isWorkingDay) return false;
    const dayStr = String(day || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dayStr)) return false;
    return dayStr <= moment().format('YYYY-MM-DD');
};

const REASON_LABELS = {
    indexation: 'Indexación / actualización de renta',
    services: 'Servicios / amenities',
    penalty: 'Penalidad',
    credit: 'Crédito / nota de crédito',
    expansion: 'Expansión / puestos extra',
    other: 'Otro',
};

const reasonLabel = (reason) => REASON_LABELS[reason] || reason || 'Sin causa';

/**
 * Modal Fixed admin:
 * - Calendario laboral (weekends + feriados por sede)
 * - Grilla mensual (empleados Fixed + Varios anónimos)
 * - Presupuesto mensual (proyectado / pagado / diferencial por sede)
 */
export const FixedPresentismoModal = ({ company, open, setOpen }) => {
    const [tab, setTab] = useState(TAB_GRID);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const [employees, setEmployees] = useState([]);
    const [offices, setOffices] = useState([]);

    const [weekendDays, setWeekendDays] = useState([6, 7]);
    const [holidays, setHolidays] = useState([]);
    const [newHolidayDate, setNewHolidayDate] = useState('');
    const [newHolidayName, setNewHolidayName] = useState('');

    const [yearMonth, setYearMonth] = useState(moment().format('YYYY-MM'));
    const [officeId, setOfficeId] = useState('');
    const [days, setDays] = useState([]);
    const [namedSet, setNamedSet] = useState({});
    const [extras, setExtras] = useState({});
    const [occupancy, setOccupancy] = useState(null);
    const [gridOffice, setGridOffice] = useState(null);
    const [chargeRows, setChargeRows] = useState([]);
    const [companyExplanations, setCompanyExplanations] = useState([]);

    const bootstrapOffices = async () => {
        const res = await AdminService.getCompanyFixedPresentismo(company.id);
        const data = res?.data || {};
        setEmployees(data.employees || []);
        setOffices(data.offices || []);
        if (!officeId && data.offices?.length) {
            setOfficeId(String(data.offices[0].id));
        }
    };

    const loadCalendar = async (nextOfficeId = officeId) => {
        if (!nextOfficeId) {
            setWeekendDays([6, 7]);
            setHolidays([]);
            return;
        }
        const res = await AdminService.getCompanyFixedWorkCalendar(company.id, nextOfficeId);
        const data = res?.data || {};
        setWeekendDays(Array.isArray(data.weekend_days) ? data.weekend_days.map(Number) : [6, 7]);
        setHolidays(data.holidays || []);
    };

    const loadMonthGrid = async (nextOfficeId = officeId, nextYearMonth = yearMonth) => {
        if (!nextOfficeId) {
            setDays([]);
            setNamedSet({});
            setExtras({});
            setOccupancy(null);
            setGridOffice(null);
            return;
        }
        const res = await AdminService.getCompanyFixedMonthGrid(company.id, nextOfficeId, nextYearMonth);
        const data = res?.data || {};
        const nextEmployees = Array.isArray(data.employees) ? data.employees : [];
        const shownIds = new Set(nextEmployees.map((e) => Number(e.id)).filter((id) => Number.isFinite(id)));
        setEmployees(nextEmployees);
        setOffices(Array.isArray(data.offices) ? data.offices : []);
        setDays(data.days || []);
        setGridOffice(data.office || null);
        setOccupancy(data.occupancy || null);

        // Incluye vigentes (editables) e históricos (solo lectura) para alinear con ocupación.
        const named = {};
        (data.named_attendance || []).forEach((row) => {
            const employeeId = Number(row?.employee_id);
            if (!Number.isFinite(employeeId) || !shownIds.has(employeeId)) return;
            named[dayCellKey(employeePersonKey(employeeId), row.day)] = true;
        });
        setNamedSet(named);
        const extraMap = {};
        (data.extras || []).forEach((row) => {
            extraMap[row.day] = Number(row.extra_count || 0);
        });
        setExtras(extraMap);
    };

    const loadMonthlyCharges = async (nextYearMonth = yearMonth) => {
        const res = await AdminService.getCompanyFixedMonthlyCharges(company.id, nextYearMonth);
        const data = res?.data || {};
        const rows = (data.rows || []).map((row) => ({
            office_id: row.office_id,
            office_name: row.office_name,
            city: row.city,
            currency: row.currency || '',
            contract_planned: Number(row.contract_planned || 0),
            planned: Number(row.planned || 0),
            planned_input: row.planned_override != null ? String(row.planned_override) : String(Number(row.planned || 0)),
            actual_input: row.actual != null ? String(row.actual) : '',
            notes: row.notes || '',
        }));
        setChargeRows(rows);
        setCompanyExplanations(Array.isArray(data.company_explanations) ? data.company_explanations : []);
    };

    useEffect(() => {
        if (!open || !company?.id) return undefined;
        let cancelled = false;
        setTab(TAB_GRID);
        setYearMonth(moment().format('YYYY-MM'));
        setError('');
        setLoading(true);
        bootstrapOffices()
            .catch((e) => {
                if (cancelled) return;
                console.log(e);
                setError('No se pudo cargar Fixed.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, company?.id]);

    useEffect(() => {
        if (!open || !company?.id) return undefined;
        let cancelled = false;
        setLoading(true);
        let run;
        if (tab === TAB_CALENDAR) {
            if (!officeId) {
                setLoading(false);
                return undefined;
            }
            run = loadCalendar(officeId);
        } else if (tab === TAB_BUDGET) {
            run = loadMonthlyCharges(yearMonth);
        } else if (!officeId) {
            setLoading(false);
            return undefined;
        } else {
            run = loadMonthGrid(officeId, yearMonth);
        }
        run
            .catch((e) => {
                if (cancelled) return;
                console.log(e);
                setError(
                    tab === TAB_CALENDAR
                        ? 'No se pudo cargar el calendario de la sede.'
                        : tab === TAB_BUDGET
                            ? 'No se pudo cargar el presupuesto mensual.'
                            : 'No se pudo cargar la grilla del mes.',
                );
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, officeId, yearMonth, open, company?.id]);

    const toggleWeekend = (dayId) => {
        setWeekendDays((prev) => (
            prev.includes(dayId) ? prev.filter((d) => d !== dayId) : [...prev, dayId].sort((a, b) => a - b)
        ));
    };

    const addHoliday = () => {
        if (!newHolidayDate || !newHolidayName.trim()) return;
        const date = newHolidayDate.slice(0, 10);
        setHolidays((prev) => {
            const without = prev.filter((h) => h.date !== date);
            return [...without, { date, name: newHolidayName.trim(), is_working_day: false }]
                .sort((a, b) => String(a.date).localeCompare(String(b.date)));
        });
        setNewHolidayDate('');
        setNewHolidayName('');
    };

    const toggleNamed = (personKey, day, isWorkingDay) => {
        if (!isAttendanceDayEditable(day, isWorkingDay)) return;
        const key = dayCellKey(personKey, day);
        setNamedSet((prev) => {
            const next = { ...prev };
            if (next[key]) delete next[key];
            else next[key] = true;
            return next;
        });
    };

    const updateChargeRow = (officeIdValue, patch) => {
        setChargeRows((prev) => prev.map((row) => (
            Number(row.office_id) === Number(officeIdValue) ? { ...row, ...patch } : row
        )));
    };

    const parseMoneyInput = (value) => {
        const raw = String(value ?? '').trim();
        if (raw === '') return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : NaN;
    };

    const handleSave = async () => {
        if (!company?.id) return;
        setSaving(true);
        setError('');
        try {
            if (tab === TAB_CALENDAR) {
                if (!officeId) {
                    setError('Elegí una sede Fixed para configurar su calendario.');
                    return;
                }
                await AdminService.upsertCompanyFixedWorkCalendar(company.id, officeId, {
                    weekend_days: weekendDays,
                    holidays: holidays.map((h) => ({
                        date: h.date,
                        name: h.name,
                        is_working_day: Boolean(h.is_working_day),
                    })),
                });
                setOpen(false);
                return;
            }

            if (tab === TAB_BUDGET) {
                const rows = [];
                for (const row of chargeRows) {
                    const planned = parseMoneyInput(row.planned_input);
                    const actual = parseMoneyInput(row.actual_input);
                    if (Number.isNaN(planned) || Number.isNaN(actual)) {
                        setError(`Monto inválido en ${row.office_name}.`);
                        return;
                    }
                    const plannedOverride =
                        planned == null || Math.abs(planned - Number(row.contract_planned || 0)) < 0.005
                            ? null
                            : planned;
                    rows.push({
                        office_id: Number(row.office_id),
                        planned_override: plannedOverride,
                        actual,
                        notes: row.notes || null,
                    });
                }
                await AdminService.upsertCompanyFixedMonthlyCharges(company.id, yearMonth, { rows });
                await loadMonthlyCharges(yearMonth);
                return;
            }

            if (!officeId) {
                setError('Elegí una sede Fixed.');
                return;
            }

            const today = moment().format('YYYY-MM-DD');
            const editableIds = new Set(
                employees
                    .filter((e) => e.editable !== false)
                    .map((e) => Number(e.id))
                    .filter((id) => Number.isFinite(id)),
            );
            const named_attendance = [];
            Object.keys(namedSet).forEach((key) => {
                // Keys are `${personKey}:${YYYY-MM-DD}`; personKey itself may contain ':'
                // (e.g. e:123), so split on the last colon only.
                const sep = key.lastIndexOf(':');
                if (sep < 0) return;
                const personKey = key.slice(0, sep);
                const day = key.slice(sep + 1);
                if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return;
                if (day > today) return;
                if (!personKey.startsWith('e:')) return;
                const employeeId = Number(personKey.slice(2));
                if (!Number.isFinite(employeeId) || !editableIds.has(employeeId)) return;
                named_attendance.push({
                    employee_id: employeeId,
                    day,
                });
            });

            await AdminService.upsertCompanyFixedMonthGrid(company.id, officeId, yearMonth, {
                named_attendance,
                extras: Object.keys(extras)
                    .filter((day) => day <= today && Number(extras[day]) > 0)
                    .map((day) => ({ day, extra_count: Number(extras[day]) })),
            });
            await loadMonthGrid(officeId, yearMonth);
        } catch (e) {
            console.log(e);
            const apiMsg =
                e?.response?.data?.message
                || e?.response?.data?.error
                || e?.message;
            const detail = Array.isArray(apiMsg) ? apiMsg.join(' ') : apiMsg;
            setError(detail ? `No se pudo guardar. ${detail}` : 'No se pudo guardar.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth={'lg'} fullWidth>
            <DialogTitle>
                Fixed
                {company?.name ? ` — ${company.name}` : ''}
            </DialogTitle>
            <DialogContent>
                <Tabs
                    value={tab}
                    onChange={(_, value) => setTab(value)}
                    indicatorColor={'primary'}
                    textColor={'primary'}
                    style={{ marginBottom: 16 }}
                >
                    <Tab label={'Calendario laboral'} />
                    <Tab label={'Grilla mensual'} />
                    <Tab label={'Presupuesto mensual'} />
                </Tabs>

                {error ? <Typography color={'error'} style={{ marginBottom: 12 }}>{error}</Typography> : null}

                {loading ? (
                    <Box style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                        <CircularProgress />
                    </Box>
                ) : null}

                {!loading && tab === TAB_CALENDAR ? (
                    <>
                        <Typography variant={'body2'} color={'textSecondary'} style={{ marginBottom: 12 }}>
                            Calendario <b>por sede</b>: una oficina en México puede tener Día de los Muertos; una en Argentina no.
                            Elegí la sede y configurá qué días no cuentan + feriados de ese país/sede.
                        </Typography>
                        <Box style={{ marginBottom: 16 }}>
                            <TextField
                                select
                                label={'Sede Fixed'}
                                value={officeId}
                                onChange={(e) => setOfficeId(e.target.value)}
                                size={'small'}
                                variant={'outlined'}
                                style={{ minWidth: 260 }}
                            >
                                {offices.map((office) => (
                                    <MenuItem key={office.id} value={String(office.id)}>
                                        {office.city ? `${office.name} (${office.city})` : office.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                        {!officeId ? (
                            <Typography color={'textSecondary'}>Elegí una sede Fixed.</Typography>
                        ) : (
                            <>
                                <Typography variant={'body2'} color={'textSecondary'} style={{ marginBottom: 8 }}>
                                    Marcá qué días de la semana <b>no cuentan</b> como laborables en esta sede (default: sáb y dom).
                                </Typography>
                                <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
                                    {WEEKDAYS.map((day) => (
                                        <FormControlLabel
                                            key={day.id}
                                            control={
                                                <Checkbox
                                                    checked={weekendDays.includes(day.id)}
                                                    onChange={() => toggleWeekend(day.id)}
                                                    color={'primary'}
                                                />
                                            }
                                            label={`${day.label} no laborable`}
                                        />
                                    ))}
                                </Box>

                                <Typography variant={'subtitle2'} style={{ marginBottom: 8 }}>Feriados de esta sede</Typography>
                                <Box style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                                    <TextField
                                        type={'date'}
                                        label={'Fecha'}
                                        InputLabelProps={{ shrink: true }}
                                        value={newHolidayDate}
                                        onChange={(e) => setNewHolidayDate(e.target.value)}
                                        size={'small'}
                                        variant={'outlined'}
                                    />
                                    <TextField
                                        label={'Nombre'}
                                        value={newHolidayName}
                                        onChange={(e) => setNewHolidayName(e.target.value)}
                                        size={'small'}
                                        variant={'outlined'}
                                    />
                                    <Button variant={'outlined'} color={'primary'} onClick={addHoliday}>Agregar</Button>
                                </Box>
                                {!holidays.length ? (
                                    <Typography color={'textSecondary'}>Sin feriados cargados en esta sede.</Typography>
                                ) : (
                                    <Table size={'small'}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Fecha</TableCell>
                                                <TableCell>Nombre</TableCell>
                                                <TableCell>Cuenta como laborable</TableCell>
                                                <TableCell />
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {holidays.map((holiday) => (
                                                <TableRow key={holiday.date}>
                                                    <TableCell>{holiday.date}</TableCell>
                                                    <TableCell>{holiday.name}</TableCell>
                                                    <TableCell>
                                                        <Checkbox
                                                            checked={Boolean(holiday.is_working_day)}
                                                            onChange={(e) => setHolidays((prev) => prev.map((h) => (
                                                                h.date === holiday.date
                                                                    ? { ...h, is_working_day: e.target.checked }
                                                                    : h
                                                            )))}
                                                            color={'primary'}
                                                        />
                                                    </TableCell>
                                                    <TableCell align={'right'}>
                                                        <IconButton
                                                            size={'small'}
                                                            onClick={() => setHolidays((prev) => prev.filter((h) => h.date !== holiday.date))}
                                                        >
                                                            <DeleteIcon fontSize={'small'} />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </>
                        )}
                    </>
                ) : null}

                {!loading && tab === TAB_GRID ? (
                    <>
                        <Typography variant={'body2'} color={'textSecondary'} style={{ marginBottom: 12 }}>
                            Asistencia por sede y mes. Los empleados vienen de la asignación Fixed de la empresa
                            (solo empleados Fixed con sede). La fila <b>Varios</b> es cupo anónimo del día.
                            Solo se pueden marcar días hasta hoy; los futuros quedan bloqueados.
                            El %: (nominados + varios) / (escritorios × días laborables hasta ayer).
                        </Typography>
                        <Box style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                            <TextField
                                select
                                label={'Sede Fixed'}
                                value={officeId}
                                onChange={(e) => setOfficeId(e.target.value)}
                                size={'small'}
                                variant={'outlined'}
                                style={{ minWidth: 220 }}
                            >
                                {offices.map((office) => (
                                    <MenuItem key={office.id} value={String(office.id)}>
                                        {office.city ? `${office.name} (${office.city})` : office.name}
                                        {` · ${office.desks || 0} puestos`}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                type={'month'}
                                label={'Mes'}
                                InputLabelProps={{ shrink: true }}
                                value={yearMonth}
                                onChange={(e) => setYearMonth(e.target.value)}
                                size={'small'}
                                variant={'outlined'}
                            />
                        </Box>

                        {occupancy ? (
                            <Typography variant={'body2'} style={{ marginBottom: 8 }}>
                                {gridOffice?.name || 'Sede'}: {occupancy.occupancy_pct}% · capacidad {occupancy.capacity}
                                {' '}({occupancy.desks} puestos × {occupancy.working_days} días laborables) ·
                                nominados {occupancy.named_present} + varios {occupancy.extra_present} = {occupancy.total_present}
                            </Typography>
                        ) : null}
                        {employees.some((e) => e.editable === false) ? (
                            <Typography variant={'caption'} color={'textSecondary'} style={{ display: 'block', marginBottom: 12 }}>
                                Filas en gris: asistencia histórica de colaboradores ya reasignados (solo lectura). Cuentan en la ocupación.
                            </Typography>
                        ) : null}

                        {!officeId ? (
                            <Typography color={'textSecondary'}>Elegí una sede Fixed.</Typography>
                        ) : !days.length ? (
                            <Typography color={'textSecondary'}>Sin días para este mes.</Typography>
                        ) : (
                            <Box
                                style={{
                                    overflow: 'auto',
                                    maxHeight: 'min(60vh, 560px)',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 4,
                                }}
                            >
                                <Table size={'small'} stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                style={{
                                                    position: 'sticky',
                                                    left: 0,
                                                    top: 0,
                                                    background: '#fafafa',
                                                    zIndex: 4,
                                                    minWidth: 160,
                                                    borderBottom: '1px solid #e0e0e0',
                                                }}
                                            >
                                                Persona
                                            </TableCell>
                                            {days.map((day) => {
                                                const editable = isAttendanceDayEditable(day.day, day.is_working_day);
                                                const dow = moment(day.day, 'YYYY-MM-DD').isoWeekday();
                                                const weekday = WEEKDAYS.find((w) => w.id === dow)?.label || '';
                                                return (
                                                <TableCell
                                                    key={day.day}
                                                    align={'center'}
                                                    style={{
                                                        position: 'sticky',
                                                        top: 0,
                                                        zIndex: 3,
                                                        minWidth: 44,
                                                        background: editable ? '#fafafa' : '#eeeeee',
                                                        color: editable ? undefined : '#999',
                                                        borderBottom: '1px solid #e0e0e0',
                                                        lineHeight: 1.15,
                                                        paddingTop: 6,
                                                        paddingBottom: 6,
                                                    }}
                                                    title={
                                                        !day.is_working_day
                                                            ? (day.holiday_name || day.day)
                                                            : (!editable ? 'Día futuro: no editable' : day.day)
                                                    }
                                                >
                                                    <Typography variant={'caption'} display={'block'} style={{ fontWeight: 600 }}>
                                                        {weekday}
                                                    </Typography>
                                                    <Typography variant={'caption'} display={'block'}>
                                                        {Number(String(day.day).slice(-2))}
                                                    </Typography>
                                                </TableCell>
                                                );
                                            })}
                                            <TableCell
                                                style={{
                                                    position: 'sticky',
                                                    top: 0,
                                                    zIndex: 3,
                                                    background: '#fafafa',
                                                    borderBottom: '1px solid #e0e0e0',
                                                }}
                                            />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {employees.map((employee) => {
                                            const personKey = employeePersonKey(employee.id);
                                            const rowEditable = employee.editable !== false;
                                            const statusLabel = rowEditable
                                                ? 'Asignado · editable'
                                                : (employee.active_office_name
                                                    ? `Solo lectura · ahora en ${employee.active_office_name}`
                                                    : 'Solo lectura · sin sede Fixed');
                                            return (
                                                <TableRow
                                                    key={personKey}
                                                    style={{ background: rowEditable ? undefined : '#f7f7f7' }}
                                                >
                                                    <TableCell
                                                        style={{
                                                            position: 'sticky',
                                                            left: 0,
                                                            background: rowEditable ? '#fff' : '#f7f7f7',
                                                            zIndex: 2,
                                                            minWidth: 160,
                                                        }}
                                                    >
                                                        <Typography variant={'body2'} noWrap style={{ maxWidth: 200 }}>
                                                            {employee.name || employee.email}
                                                        </Typography>
                                                        <Typography variant={'caption'} color={'textSecondary'}>
                                                            {statusLabel}
                                                        </Typography>
                                                    </TableCell>
                                                    {days.map((day) => {
                                                        const dayEditable = isAttendanceDayEditable(day.day, day.is_working_day);
                                                        const canEdit = rowEditable && dayEditable;
                                                        return (
                                                        <TableCell
                                                            key={day.day}
                                                            align={'center'}
                                                            style={{
                                                                background: canEdit ? undefined : '#f0f0f0',
                                                                padding: 0,
                                                            }}
                                                        >
                                                            <Checkbox
                                                                size={'small'}
                                                                color={'primary'}
                                                                disabled={!canEdit}
                                                                checked={Boolean(namedSet[dayCellKey(personKey, day.day)])}
                                                                onChange={() => {
                                                                    if (!rowEditable) return;
                                                                    toggleNamed(personKey, day.day, day.is_working_day);
                                                                }}
                                                            />
                                                        </TableCell>
                                                        );
                                                    })}
                                                    <TableCell />
                                                </TableRow>
                                            );
                                        })}
                                        <TableRow>
                                            <TableCell
                                                style={{
                                                    position: 'sticky',
                                                    left: 0,
                                                    background: '#fff',
                                                    zIndex: 2,
                                                    fontWeight: 600,
                                                    minWidth: 160,
                                                }}
                                            >
                                                Varios / sin nombre
                                            </TableCell>
                                            {days.map((day) => {
                                                const editable = isAttendanceDayEditable(day.day, day.is_working_day);
                                                return (
                                                <TableCell
                                                    key={day.day}
                                                    align={'center'}
                                                    style={{ background: editable ? undefined : '#f0f0f0', padding: 4 }}
                                                >
                                                    <TextField
                                                        type={'number'}
                                                        value={extras[day.day] ?? ''}
                                                        disabled={!editable}
                                                        onChange={(e) => {
                                                            const value = e.target.value === '' ? '' : Math.max(0, Number(e.target.value) || 0);
                                                            setExtras((prev) => ({ ...prev, [day.day]: value }));
                                                        }}
                                                        inputProps={{ min: 0, style: { width: 40, textAlign: 'center', padding: 4 } }}
                                                    />
                                                </TableCell>
                                                );
                                            })}
                                            <TableCell />
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Box>
                        )}
                    </>
                ) : null}

                {!loading && tab === TAB_BUDGET ? (
                    <>
                        <Typography variant={'body2'} color={'textSecondary'} style={{ marginBottom: 12 }}>
                            Presupuesto mensual por sede/contrato: <b>proyectado</b> (desde el contrato),
                            <b> pagado</b> y <b>nota Offi</b> (los carga admin).
                            La <b>explicación empresa</b> la escribe el cliente en la web (solo lectura acá).
                        </Typography>
                        <Box style={{ marginBottom: 16 }}>
                            <TextField
                                type={'month'}
                                label={'Mes'}
                                InputLabelProps={{ shrink: true }}
                                value={yearMonth}
                                onChange={(e) => setYearMonth(e.target.value)}
                                size={'small'}
                                variant={'outlined'}
                            />
                        </Box>
                        {!chargeRows.length ? (
                            <Typography color={'textSecondary'}>No hay sedes Fixed para esta empresa.</Typography>
                        ) : (
                            <Table size={'small'}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Sede</TableCell>
                                        <TableCell>Moneda</TableCell>
                                        <TableCell align={'right'}>Proyectado</TableCell>
                                        <TableCell align={'right'}>Pagado</TableCell>
                                        <TableCell align={'right'}>Diferencial</TableCell>
                                        <TableCell>Nota Offi</TableCell>
                                        <TableCell>Explicación empresa</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {chargeRows.map((row) => {
                                        const planned = parseMoneyInput(row.planned_input);
                                        const actual = parseMoneyInput(row.actual_input);
                                        const deviation =
                                            planned == null || actual == null || Number.isNaN(planned) || Number.isNaN(actual)
                                                ? null
                                                : Math.round((actual - planned) * 100) / 100;
                                        const companyNotesRaw = companyExplanations.filter(
                                            (n) =>
                                                n.office_id == null
                                                || Number(n.office_id) === Number(row.office_id),
                                        );
                                        const companyNotes = [...companyNotesRaw]
                                            .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))
                                            .slice(0, 1);
                                        return (
                                            <TableRow key={row.office_id}>
                                                <TableCell>
                                                    <Typography variant={'body2'}>
                                                        {row.city ? `${row.office_name} (${row.city})` : row.office_name}
                                                    </Typography>
                                                    <Typography variant={'caption'} color={'textSecondary'}>
                                                        Contrato: {Number(row.contract_planned || 0).toLocaleString('es-AR')}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{row.currency || '—'}</TableCell>
                                                <TableCell align={'right'}>
                                                    <TextField
                                                        type={'number'}
                                                        value={row.planned_input}
                                                        onChange={(e) => updateChargeRow(row.office_id, { planned_input: e.target.value })}
                                                        size={'small'}
                                                        variant={'outlined'}
                                                        inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right', width: 110 } }}
                                                    />
                                                </TableCell>
                                                <TableCell align={'right'}>
                                                    <TextField
                                                        type={'number'}
                                                        value={row.actual_input}
                                                        onChange={(e) => updateChargeRow(row.office_id, { actual_input: e.target.value })}
                                                        size={'small'}
                                                        variant={'outlined'}
                                                        placeholder={'Sin cargar'}
                                                        inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right', width: 110 } }}
                                                    />
                                                </TableCell>
                                                <TableCell align={'right'}>
                                                    <Typography
                                                        variant={'body2'}
                                                        style={{
                                                            color: deviation == null
                                                                ? undefined
                                                                : deviation > 0
                                                                    ? '#c62828'
                                                                    : deviation < 0
                                                                        ? '#2e7d32'
                                                                        : undefined,
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {deviation == null
                                                            ? '—'
                                                            : `${deviation > 0 ? '+' : ''}${deviation.toLocaleString('es-AR')}`}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell style={{ minWidth: 160 }}>
                                                    <TextField
                                                        value={row.notes}
                                                        onChange={(e) => updateChargeRow(row.office_id, { notes: e.target.value })}
                                                        size={'small'}
                                                        variant={'outlined'}
                                                        fullWidth
                                                        placeholder={'Nota Offi (opcional)'}
                                                    />
                                                </TableCell>
                                                <TableCell style={{ minWidth: 200, maxWidth: 280 }}>
                                                    {!companyNotes.length ? (
                                                        <Typography variant={'caption'} color={'textSecondary'}>
                                                            Sin explicación de la empresa
                                                        </Typography>
                                                    ) : (
                                                        companyNotes.map((n) => (
                                                            <Box key={n.id} style={{ marginBottom: 6 }}>
                                                                <Typography variant={'caption'} style={{ fontWeight: 600, display: 'block' }}>
                                                                    {reasonLabel(n.reason)}
                                                                    {n.created_by_name ? ` · ${n.created_by_name}` : ''}
                                                                </Typography>
                                                                <Typography variant={'body2'} style={{ whiteSpace: 'pre-wrap' }}>
                                                                    {n.comment}
                                                                </Typography>
                                                            </Box>
                                                        ))
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        )}
                    </>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpen(false)} disabled={saving}>Cerrar</Button>
                <Button color={'primary'} variant={'contained'} onClick={handleSave} disabled={loading || saving}>
                    {saving ? 'Guardando…' : 'Guardar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FixedPresentismoModal;
